import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { formatDateTimeFull } from 'src/util/i18n/helpers';

import AdminPrimaryBillingPointShowMeter from './AdminPrimaryBillingPointShowMeter';
// import AdminPrimaryBillingPointShowTariff from './AdminPrimaryBillingPointShowTariff';

/**
 * Description
 * @param {object} props
 * @param {object} props.primaryBillingPoint
 * @returns {React.ReactComponentElement} - AdminPrimaryBillingPoint component
 */
function AdminPrimaryBillingPoint({ primaryBillingPoint = null }) {
  const intl = useIntl();

  if (!primaryBillingPoint) {
    return <Loading />;
  }

  const {
    uuid, externalIdentifier, active, identifier, property, createdAt, updatedAt, meters,
  } = primaryBillingPoint;
  const { start, finish } = active;

  // Page title and breadcrumbs
  const pageTitle = intl.formatMessage(
    {
      id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.page.title',
      defaultMessage: 'Enosi Admin - Primary Billing Point - {identifier}',
    },
    { identifier },
  );
  const breadcrumbAdmin = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.breadcrumbs.admin.label',
    defaultMessage: 'Admin',
  });
  const breadcrumbPrimaryBillingPoints = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.breadcrumbs.primary_billing_points.label',
    defaultMessage: 'Primary Billing Points',
  });

  // Card headers
  const detailsCardHeader = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.card.details.header',
    defaultMessage: 'Primary Billing Point details',
  });
  const metersCardHeader = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.card.meters.header',
    defaultMessage: 'Meters',
  });

  // Table headers - Details
  const tableHeaderId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.id',
    defaultMessage: 'ID',
  });
  const tableHeaderExternalIdentifier = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.external_identifier',
    defaultMessage: 'External identifier',
  });
  const tableHeaderActiveStatus = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.active_status',
    defaultMessage: 'Active status',
  });
  const tableHeaderStart = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.start',
    defaultMessage: 'Start',
  });
  const tableHeaderFinish = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.finish',
    defaultMessage: 'Finish',
  });
  const tableHeaderIdentifier = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.identifier',
    defaultMessage: 'Identifier',
  });
  const tableHeaderProperty = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.property',
    defaultMessage: 'Property',
  });
  const tableHeaderCreatedAt = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.created_at',
    defaultMessage: 'Created at',
  });
  const tableHeaderUpdatedAt = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.table.header.updated_at',
    defaultMessage: 'Updated at',
  });

  // Table headers - Meters
  const metersTableHeaderIdentifier = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.meters.table.header.identifier',
    defaultMessage: 'Identifier',
  });
  const metersTableHeaderId = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.meters.table.header.id',
    defaultMessage: 'ID',
  });
  const metersTableHeaderActive = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.meters.table.header.active',
    defaultMessage: 'Active',
  });
  const metersTableHeaderPrimaryBillingPoint = intl.formatMessage({
    id: 'admin.admin_primary_billing_point.admin_primary_billing_point_show.meters.table.header.primary_billing_point',
    defaultMessage: 'Primary Billing Point',
  });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPrimaryBillingPoints, path: '/admin/primary-billing-points' },
          { name: identifier },
        ]}
      />

      <h1>
        {identifier}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {detailsCardHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderId}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderExternalIdentifier}</th>
                  <td>{externalIdentifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderActiveStatus}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderStart}</th>
                  <td>
                    {start && formatDateTimeFull(intl, start)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderFinish}</th>
                  <td>
                    {finish && formatDateTimeFull(intl, finish)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderIdentifier}</th>
                  <td>{identifier}</td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderProperty}</th>
                  <td>
                    <Link to={`/admin/properties/${property.uuid}`}>
                      <strong>{property && property.title}</strong>
                    </Link>
                    <br />
                    <UUID uuid={property && property.uuid} />
                    <br />
                    <BadgeActive start={property.active.start} finish={property.active.finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderCreatedAt}</th>
                  <td>
                    {createdAt
                      && formatDateTimeFull(intl, createdAt)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{tableHeaderUpdatedAt}</th>
                  <td>
                    {updatedAt
                      && formatDateTimeFull(intl, updatedAt)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card className="mb-4">
            <CardHeader>
              {metersCardHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{metersTableHeaderIdentifier}</th>
                  <th scope="col">
                    <abbr title={metersTableHeaderId}>{metersTableHeaderId}</abbr>
                  </th>
                  <th scope="col">{metersTableHeaderActive}</th>
                  <th scope="col">{metersTableHeaderPrimaryBillingPoint}</th>
                </tr>
              </thead>
              <tbody>
                {meters && meters.edges.map((el) => (
                  <AdminPrimaryBillingPointShowMeter meter={el.node} key={el.node.uuid} />
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminPrimaryBillingPoint.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  primaryBillingPoint: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

export default createFragmentContainer(
  AdminPrimaryBillingPoint,
  {
    primaryBillingPoint: graphql`
      fragment AdminPrimaryBillingPointShow_primaryBillingPoint on PrimaryBillingPoint {
        uuid
        externalIdentifier
        active { start finish }
        identifier
        property {
          uuid
          title
          active {
            start
            finish
          }
        }
        createdAt
        updatedAt
        meters(first: 500) {
          edges {
            node {
              uuid
              identifier
              active { start finish }
              primaryBillingPoint {
                uuid
                identifier
              }
            }
          }
        }
      }
    `,
  },
);
